import { useEffect, useState, useRef } from 'react';
import {
  downloadEncryptedBlob,
  decryptBlobFromStream,
} from '../lib/attachments';
import sodium from 'libsodium-wrappers';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * AttachmentViewer — Sprint 3.
 *
 * Apre, decifra, mostra un attachment one-view. Dopo che l'utente lo ha visto:
 *  - Marca markMessageViewed (server schedula delete preciso via Cloud Tasks)
 *  - Libera l'URL del blob
 *  - Mostra "Visto. Sparito."
 *
 * Anti-screenshot: 
 *  - Su Android nativo (Capacitor futuro): FLAG_SECURE
 *  - Su web: l'immagine è in <img src="blob:..."> non scaricabile da right-click facilmente
 *           ma uno screenshot del sistema è sempre possibile (limite del web)
 */

// Quanto tempo l'allegato resta APERTO sul viewer del destinatario, derivato
// dal TTL scelto dal MITTENTE (lui decide, non chi riceve):
//   senderTTL = 0   → 5 s di anteprima e brucia (zero-persist)
//   senderTTL = 10  → 10 s
//   senderTTL = 300 → 5 min
//   senderTTL = 3600 → 1 ora
//   senderTTL = -1 (premium "mai") → niente auto-burn, l'utente chiude a mano
function viewerSecondsFromTTL(ttl) {
  if (ttl === -1) return null;   // mai
  if (ttl === 0) return 5;       // zero-persist: 5s preview
  if (typeof ttl === 'number' && ttl > 0) return ttl;
  return 10;                     // default safe
}

export default function AttachmentViewer({ message, chatId, attachmentKey, onClose, selfView = false }) {
  const [phase, setPhase] = useState('loading'); // loading | viewing | burned | error
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState('');
  const timerRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Countdown viewing = TTL imposto dal mittente, SEMPRE (anche per gli
  // allegati scaricabili: il TTL vale lo stesso, semplicemente il
  // destinatario puo' salvarne una copia prima che bruci).
  const totalSecondsRef = useRef(viewerSecondsFromTTL(message?.senderTTL));
  const [secondsLeft, setSecondsLeft] = useState(totalSecondsRef.current);
  const tickRef = useRef(null);
  const [zoom, setZoom] = useState(false); // immagine: tap per zoom/pan

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // Scarico il blob e inizializzo sodium IN PARALLELO (il download non
        // dipende da sodium) → apertura più rapida.
        const [, encryptedBlob] = await Promise.all([
          sodium.ready,
          downloadEncryptedBlob(chatId, message.id, message.kind || 'image'),
        ]);
        if (cancelled) return;

        // 2. Decifra
        const mimeType = message.attachmentMime || 'image/jpeg';
        const decryptedBlob = await decryptBlobFromStream(encryptedBlob, attachmentKey, mimeType);
        if (cancelled) return;

        // 3. URL temporaneo
        const url = URL.createObjectURL(decryptedBlob);
        blobUrlRef.current = url;
        setBlobUrl(url);
        setPhase('viewing');

        // 4. Marca come visto → schedula Cloud Task per delete preciso.
        //    NON quando sto rivedendo un MIO allegato (selfView): la lettura del
        //    destinatario è ciò che avvia il TTL/burn, non la mia.
        if (!selfView) {
          const markFn = httpsCallable(functions, 'markMessageViewed');
          await markFn({ chatId, msgId: message.id });
        }

      } catch (err) {
        console.error('Attachment viewer error', err);
        if (!cancelled) {
          setError(err.message || 'Errore caricamento');
          setPhase('error');
        }
      }
    }

    load();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [chatId, message.id, message.attachmentMime, attachmentKey]);

  // Quando passo a 'viewing' attivo il countdown imposto dal mittente.
  // Su 0 chiamo handleBurn. Se senderTTL = -1 (premium "mai") non parte
  // niente — l'utente chiude a mano.
  useEffect(() => {
    if (phase !== 'viewing') return;
    if (selfView) return; // rivedo un MIO allegato: nessun auto-burn, chiudo a mano
    const total = totalSecondsRef.current;
    if (total == null) return; // mai
    setSecondsLeft(total);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tickRef.current);
          tickRef.current = null;
          // burn al prossimo tick di event loop per evitare setState durante render
          setTimeout(() => handleBurn(), 0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  function handleBurn() {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setBlobUrl(null);
    setPhase('burned');
    setTimeout(() => { if (onClose) onClose(); }, 1500);
  }

  function fmtRemaining(s) {
    if (s == null) return '∞';
    if (s >= 60) {
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `${m}:${String(r).padStart(2, '0')}`;
    }
    return `${s}s`;
  }

  if (phase === 'loading') {
    return (
      <Overlay>
        <div className="text-emerald-400">Decifratura in corso...</div>
      </Overlay>
    );
  }

  if (phase === 'error') {
    return (
      <Overlay onClose={onClose}>
        <div className="text-red-400">Errore: {error}</div>
      </Overlay>
    );
  }

  if (phase === 'burned') {
    return (
      <Overlay>
        <div className="text-center space-y-2">
          <div className="text-6xl">🔥</div>
          <div className="text-amber-400 font-mono">Visto. Sparito.</div>
        </div>
      </Overlay>
    );
  }

  // phase === 'viewing'
  const mime = message.attachmentMime || 'image/jpeg';
  const isVideo = message.kind === 'video' || mime.startsWith('video/');
  const isAudio = message.kind === 'audio' || mime.startsWith('audio/');
  const isImage = !isVideo && !isAudio && (message.kind === 'image' || mime.startsWith('image/'));
  const isFile = !isVideo && !isAudio && !isImage;
  const fileName = message.attachmentName || 'file';
  // Il mittente ha reso questo allegato scaricabile? (default: no, one-view)
  const downloadable = message.downloadable === true;
  const dlName = fileName !== 'file'
    ? fileName
    : `nullum-${isVideo ? 'video.mp4' : isAudio ? 'audio' : 'immagine.jpg'}`;

  return (
    <Overlay>
      <div className="space-y-4 w-full max-w-5xl mx-auto">
        {isVideo ? (
          <video
            src={blobUrl}
            autoPlay
            controls
            playsInline
            className="w-full max-h-[88vh] mx-auto bg-black"
          />
        ) : isAudio ? (
          <div className="text-center space-y-3">
            <div className="text-5xl">🎙</div>
            <audio
              src={blobUrl}
              autoPlay
              controls
              className="mx-auto block w-full max-w-md"
            />
          </div>
        ) : isFile ? (
          <div className="text-center space-y-3">
            <div className="text-5xl">📎</div>
            <div className="text-emerald-300 break-all">{fileName}</div>
            <a
              href={blobUrl}
              download={fileName}
              className="inline-block px-6 py-3 bg-emerald-700 hover:bg-emerald-600 rounded font-bold transition"
            >
              Scarica il file
            </a>
          </div>
        ) : (
          <div
            className="w-full overflow-auto mx-auto"
            style={{ maxHeight: '88vh', touchAction: 'pinch-zoom' }}
          >
            <img
              src={blobUrl}
              alt=""
              onClick={() => setZoom((z) => !z)}
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
              style={{
                display: 'block',
                margin: '0 auto',
                cursor: zoom ? 'zoom-out' : 'zoom-in',
                width: zoom ? 'auto' : '100%',
                maxWidth: zoom ? 'none' : '100%',
                maxHeight: zoom ? 'none' : '88vh',
                objectFit: 'contain',
              }}
            />
            <div className="text-center text-zinc-500 text-xs mt-1">
              tocca l'immagine per {zoom ? 'ridurre' : 'ingrandire'} · pizzica per lo zoom
            </div>
          </div>
        )}

        <div className="text-center">
          {downloadable && (
            <div className="mb-1 text-emerald-400 font-mono text-sm">
              📥 salvabile — fai una copia prima che bruci
            </div>
          )}
          {totalSecondsRef.current != null ? (
            <div className="mb-3 text-amber-400 font-mono text-lg tracking-wider">
              🔥 brucia tra: {fmtRemaining(secondsLeft)}
            </div>
          ) : (
            <div className="mb-3 text-zinc-400 font-mono text-sm">
              il mittente non ha imposto un timer{downloadable ? '' : ' · chiudi quando hai finito'}
            </div>
          )}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {downloadable && !isFile && (
              <a
                href={blobUrl}
                download={dlName}
                className="px-6 py-3 bg-emerald-700 hover:bg-emerald-600 rounded font-bold transition"
              >
                💾 Salva
              </a>
            )}
            <button
              onClick={handleBurn}
              className="px-6 py-3 bg-amber-600 hover:bg-amber-500 rounded font-bold transition"
            >
              {downloadable ? 'Chiudi' : 'Chiudi prima'}
            </button>
          </div>
          <div className="text-xs text-zinc-500 mt-2">
            {downloadable
              ? 'puoi salvare una copia sul tuo dispositivo.'
              : 'il mittente decide quanto tempo puoi tenerlo aperto. Una volta chiuso o scaduto il timer, non sarà più disponibile.'}
          </div>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
      <div className="w-full">
        {children}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-white text-2xl"
        >
          ✕
        </button>
      )}
    </div>
  );
}
